import { useCallback, useRef } from 'react';
import { preloadRoute } from '@/utils/routePreloader';

interface UseHoverPreloadOptions {
  delay?: number;
}

/**
 * Hook for hover-based route preloading
 * Preloads route components when user hovers over a link
 */
export const useHoverPreload = (options: UseHoverPreloadOptions = {}) => {
  const { delay = 50 } = options;
  const timeoutRef = useRef<NodeJS.Timeout>();
  const preloadedRoutes = useRef<Set<string>>(new Set());

  const handleMouseEnter = useCallback((routePath: string) => {
    // Skip if already preloaded
    if (preloadedRoutes.current.has(routePath)) {
      return;
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Delay preload slightly to avoid preloading on accidental hovers
    timeoutRef.current = setTimeout(async () => {
      try {
        await preloadRoute(routePath);
        preloadedRoutes.current.add(routePath);
        console.log(`[HoverPreload] Preloaded route: ${routePath}`);
      } catch (err) {
        console.warn(`[HoverPreload] Failed to preload ${routePath}:`, err);
      }
    }, delay);
  }, [delay]);

  const handleMouseLeave = useCallback(() => {
    // Cancel preload if user moves away before delay completes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const getPreloadHandlers = useCallback((routePath: string) => ({
    onMouseEnter: () => handleMouseEnter(routePath),
    onMouseLeave: handleMouseLeave,
    onFocus: () => handleMouseEnter(routePath), // Also preload on keyboard focus
  }), [handleMouseEnter, handleMouseLeave]);

  return {
    getPreloadHandlers,
    handleMouseEnter,
    handleMouseLeave,
  };
};
